import { DiscreteEvent, dispatchCustomEvent, KPI } from '@utils/customEvents'
import type { Enrollment } from '@clear-denver/tpc-openapi-typescript'
import { session } from './session'
import { formatReason, formatMeta } from './utils'
import {
  LookupPipeMeta,
  ResultMeta,
  SearchResult,
  DisambiguationResult,
  SendOTPResult,
  VerifyOTPResult,
  LookupResult,
  ResultShownResult,
  LookupDiscreteEvent,
  PREFIX,
} from './types'

const BASE_META_KEYS: (keyof LookupPipeMeta)[] = [
  'xCLEARCorrelationInfo',
  'lookupFlowSessionId',
  'flow',
  'searchType',
  'route',
  'flagEnabled',
]

export function createLookupPipe(meta: LookupPipeMeta) {
  session.meta = meta
  const baseMeta = {
    ...meta,
    kpi: KPI.USER_INTERACTION,
  }

  function updateBaseMeta(props: ResultMeta) {
    for (const key of BASE_META_KEYS) {
      if (key in props && props[key] != null) {
        ;(baseMeta as Record<string, unknown>)[key] = props[key]
      }
    }
  }

  function info(opts: {
    message: string
    event: DiscreteEvent
    step: string
    code?: string
    meta?: ResultMeta
  }) {
    if (opts.meta) updateBaseMeta(opts.meta)
    const payload = {
      payload: { message: PREFIX + opts.message },
      metadata: {
        ...baseMeta,
        ...opts.meta,
        step: opts.step,
        ...(opts.code ? { code: opts.code } : {}),
        event: opts.event,
      },
    }
    if (session.debug) console.info(`[LOOKUP:${opts.event}]`, payload)
    queueMicrotask(() => {
      dispatchCustomEvent('INFO' as Enrollment.LogEntryLevelEnum.INFO, payload)
    })
  }

  function fail(opts: {
    step: string
    code: string
    reason: string
    event?: DiscreteEvent
    meta?: ResultMeta
  }) {
    if (opts.meta) updateBaseMeta(opts.meta)
    session.trackEvent(`fail:${opts.code}`)
    const payload = {
      payload: { message: PREFIX + opts.reason },
      metadata: {
        ...baseMeta,
        ...opts.meta,
        step: opts.step,
        code: opts.code,
        reason: opts.reason,
        event: opts.event ?? DiscreteEvent.CLIENT_SIDE_ERROR,
      },
    }
    if (session.debug) console.error(`[LOOKUP:${opts.code}]`, payload)
    queueMicrotask(() => {
      dispatchCustomEvent(
        'ERROR' as Enrollment.LogEntryLevelEnum.ERROR,
        payload
      )
    })
  }

  return {
    view: (meta?: ResultMeta) => {
      session.resetSession()
      baseMeta.lookupFlowSessionId = session.lookupFlowSessionId
      if (meta && Object.keys(meta).length) updateBaseMeta(meta)
      session.meta = { ...(baseMeta as unknown as LookupPipeMeta) }
      session.trackEvent('view')
      info({
        message: 'Lookup page viewed',
        event: LookupDiscreteEvent.LOOKUP_VIEW,
        step: 'VIEW',
        meta,
      })
    },

    search: (result: SearchResult) => {
      session.trackEvent(`search:${result.status}`)
      switch (result.status) {
        case 'begin': {
          const { status: _, searchType, ...meta } = result
          session.searchType = searchType
          baseMeta.searchType = searchType
          info({
            message: `Search by ${searchType} begin`,
            event: LookupDiscreteEvent.LOOKUP_SEARCH_BEGIN,
            step: 'SEARCH',
            meta: { searchType, ...meta },
          })
          break
        }
        case 'success': {
          const {
            status: _,
            code,
            hasOtpSessionId,
            needCommMethod,
            disambiguation,
            ...meta
          } = result
          info({
            message: 'Search successful',
            event: LookupDiscreteEvent.LOOKUP_SEARCH_SUCCESS,
            step: 'SEARCH',
            code,
            meta: {
              code,
              hasOtpSessionId: String(hasOtpSessionId),
              needCommMethod: String(!!needCommMethod),
              disambiguation: String(!!disambiguation),
              ...meta,
            },
          })
          break
        }
        case 'failure': {
          const { status: _, error, code, ...meta } = result
          fail({
            step: 'SEARCH',
            code,
            event: LookupDiscreteEvent.LOOKUP_SEARCH_FAILURE,
            reason: formatReason(error),
            meta: { code, ...formatMeta(error), ...meta },
          })
          break
        }
        default: {
          throw new Error(
            JSON.stringify(
              {
                step: 'SEARCH',
                reason: `Status not supported: ${
                  (result as Record<string, unknown>).status
                }`,
              },
              null,
              2
            )
          )
        }
      }
    },

    disambiguation: (result: DisambiguationResult) => {
      session.trackEvent(`disambiguation:${result.status}`)
      switch (result.status) {
        case 'begin': {
          const { status: _, ...meta } = result
          const count = session.trackDisambiguation()
          info({
            message: 'Disambiguation form shown',
            event: LookupDiscreteEvent.LOOKUP_DISAMBIGUATION_BEGIN,
            step: 'DISAMBIGUATION',
            meta: {
              attemptNumber: String(count),
              disambiguationCount: String(count),
              ...meta,
            },
          })
          break
        }
        case 'success': {
          const { status: _, code, ...meta } = result
          info({
            message: 'Disambiguation resolved',
            event: LookupDiscreteEvent.LOOKUP_DISAMBIGUATION_SUCCESS,
            step: 'DISAMBIGUATION',
            code: code ?? 'DISAMBIGUATION_RESOLVED',
            meta: {
              ...(code ? { code } : {}),
              disambiguationCount: String(session.disambiguationCount),
              ...meta,
            },
          })
          break
        }
        case 'failure': {
          const { status: _, error, code, ...meta } = result
          fail({
            step: 'DISAMBIGUATION',
            code,
            event: LookupDiscreteEvent.LOOKUP_DISAMBIGUATION_FAILURE,
            reason: formatReason(error),
            meta: {
              code,
              disambiguationCount: String(session.disambiguationCount),
              ...formatMeta(error),
              ...meta,
            },
          })
          break
        }
        default: {
          throw new Error(
            JSON.stringify(
              {
                step: 'DISAMBIGUATION',
                reason: `Status not supported: ${
                  (result as Record<string, unknown>).status
                }`,
              },
              null,
              2
            )
          )
        }
      }
    },

    sendOTP: (result: SendOTPResult) => {
      session.trackEvent(`sendOTP:${result.status}`)
      switch (result.status) {
        case 'begin': {
          const { status: _, ...meta } = result
          info({
            message: 'Send OTP begin',
            event: LookupDiscreteEvent.LOOKUP_SEND_OTP_BEGIN,
            step: 'OTP',
            meta,
          })
          break
        }
        case 'success': {
          const { status: _, code, ...meta } = result
          info({
            message: 'Send OTP successful',
            event: LookupDiscreteEvent.LOOKUP_SEND_OTP_SUCCESS,
            step: 'OTP',
            code: code ?? 'OTP_DELIVERED',
            meta: { ...(code ? { code } : {}), ...meta },
          })
          break
        }
        case 'failure': {
          const { status: _, error, code, ...meta } = result
          fail({
            step: 'OTP',
            code,
            event: LookupDiscreteEvent.LOOKUP_SEND_OTP_FAILURE,
            reason: formatReason(error),
            meta: { code, ...formatMeta(error), ...meta },
          })
          break
        }
        default: {
          throw new Error(
            JSON.stringify(
              {
                step: 'OTP',
                reason: `Status not supported: ${
                  (result as Record<string, unknown>).status
                }`,
              },
              null,
              2
            )
          )
        }
      }
    },

    verifyOTP: (result: VerifyOTPResult) => {
      session.trackEvent(`verifyOTP:${result.status}`)
      switch (result.status) {
        case 'begin': {
          const { status: _, ...meta } = result
          info({
            message: 'OTP verification begin',
            event: LookupDiscreteEvent.LOOKUP_VERIFY_OTP_BEGIN,
            step: 'OTP',
            meta,
          })
          break
        }
        case 'success': {
          const { status: _, code, ...meta } = result
          info({
            message: 'OTP verification successful',
            event: LookupDiscreteEvent.LOOKUP_VERIFY_OTP_SUCCESS,
            step: 'OTP',
            code: code ?? 'OTP_VERIFIED',
            meta: {
              ...(code ? { code } : {}),
              attempts: String(session.otpAttempts),
              ...meta,
            },
          })
          break
        }
        case 'failure': {
          const { status: _, error, code, ...meta } = result
          const attempts = session.trackOtpAttempt()
          fail({
            step: 'OTP',
            code,
            event: LookupDiscreteEvent.LOOKUP_VERIFY_OTP_FAILURE,
            reason: formatReason(error),
            meta: {
              code,
              attempts: String(attempts),
              ...formatMeta(error),
              ...meta,
            },
          })
          break
        }
        default: {
          throw new Error(
            JSON.stringify(
              {
                step: 'OTP',
                reason: `Status not supported: ${
                  (result as Record<string, unknown>).status
                }`,
              },
              null,
              2
            )
          )
        }
      }
    },

    lookup: (result: LookupResult) => {
      session.trackEvent(`lookup:${result.status}`)
      switch (result.status) {
        case 'begin': {
          const { status: _, ...meta } = result
          info({
            message: 'Verified lookup begin',
            event: LookupDiscreteEvent.LOOKUP_RESULT_BEGIN,
            step: 'LOOKUP',
            meta,
          })
          break
        }
        case 'success': {
          const { status: _, code, ...meta } = result
          session.resultVariant = code
          info({
            message: 'Verified lookup successful',
            event: LookupDiscreteEvent.LOOKUP_RESULT_SUCCESS,
            step: 'LOOKUP',
            code,
            meta: { code, ...meta },
          })
          break
        }
        case 'failure': {
          const { status: _, error, code, ...meta } = result
          fail({
            step: 'LOOKUP',
            code,
            event: LookupDiscreteEvent.LOOKUP_RESULT_FAILURE,
            reason: formatReason(error),
            meta: { code, ...formatMeta(error), ...meta },
          })
          break
        }
        default: {
          throw new Error(
            JSON.stringify(
              {
                step: 'LOOKUP',
                reason: `Status not supported: ${
                  (result as Record<string, unknown>).status
                }`,
              },
              null,
              2
            )
          )
        }
      }
    },

    result: (data: ResultShownResult) => {
      const { code, ...meta } = data
      session.resultVariant = code
      session.trackEvent(`result:${code}`)
      info({
        message: 'Result card displayed',
        event: LookupDiscreteEvent.LOOKUP_RESULT_SHOWN,
        step: 'RESULT',
        code,
        meta: { code, ...meta },
      })
    },

    conclude: () => {
      session.concluded = true
      info({
        message: 'Lookup flow concluded',
        event: LookupDiscreteEvent.LOOKUP_FLOW_CONCLUDED,
        step: 'CONCLUDED',
        code: 'CONCLUDED',
      })
      session.resetSession()
    },
  }
}

export type LookupPipe = ReturnType<typeof createLookupPipe>
